import _ from "lodash";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";

import {
    Button,
    Col, ConnectedPlusIcon,
    Popup,
    Row,
    SkubotLogo,
    StateDropdown,
    StatusDropdown, Table, TBody, TD, TH,
    Theme, TR,
    XIcon
} from "@commonsku/styles";
import { formatDateSafe,parseMysqlDate, oauth} from "../utils";

const toArray = (v) => {
  return _.isArray(v) ? v : [v];
};

const checkOrderStatus = async (purchase_order_id, division_id, reference_number, update_ps_status=false) => {
  const data = {
    'purchase_order_id': purchase_order_id,
    'division_id': division_id,
    'reference_number': reference_number,
    'query_type': 1,
    'action': 'order_status',
    'update_ps_status': update_ps_status
  };
  const response = await oauth('POST', 'promo-standards', data);
  const responseJson = response.json || {};
  const { status, ship_notification } = responseJson;
  const error = responseJson.error || _.get(status, "error", "");
  const statuses = {};
  (toArray((status || {}).order_status || [])).forEach((os) => {
    return toArray(_.get(os, 'OrderStatusDetailArray.OrderStatusDetail') || []).forEach((osd) => {
      statuses[osd.factoryOrderNumber] = osd;
    });
  });
  return {
    error,
    statuses: _.values(statuses),
    notifications: toArray(_.get(ship_notification, 'SalesOrderArray.SalesOrder') || []),
    ps_status_name: _.get(status, 'new_po_status', ''),
    date_valid: _.get(status, 'new_date_valid', ''),
  };
};

const StyledTHead = styled.thead`
    &&& {
        tr:first-child th
        {
            background-color: #edf4f7;
        }
    }
`;

const Address = ({ address }) => {
  const {
    address1, address2, address3, address4, city, region, postalCode, country
  }  = address;
  return <>{
    _.flatMap(
      _.filter([address1, address2, address3, address4, _.filter([city, region, postalCode, country]).join(' ')]),
      (line) => [line, <br/>]
    )
  }</>;
};

function OrderStatusPopup({data = {}, tableStandings = [], tableStatuses = [], recommendation, commonskuStatuses, onChange, onPatchRow, statusIcon, onClose }) {
  const normalizedData = {
    ...data,
    division_id: data.division_id || data.supplier_id, 
    status_id: data.status_id || data?.status?.status_id,
    status_name: data.status_name || data?.status?.status_name,
  };
  const [statuses, setStatuses] = useState([]);
  const [notifications, setNotifications] = useState([]);
    
  const [psStatusName, setPsStatusName] = useState(normalizedData.ps_status_name || '');
  const [dateValid, setDateValid] = useState(normalizedData.date_valid || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Support supplier_id as alias for division_id
  const { purchase_order_id, form_number, division_id, date_shipped} = normalizedData;

    useEffect(() => {
      setLoading(true);
      checkOrderStatus(purchase_order_id, division_id, form_number).then((result) => {
        setStatuses(result.statuses);
        setNotifications(result.notifications);
        setError(result.error);
      }).catch((e) => {  
        setStatuses([]);
        setNotifications([]);
        setError(e?.message || 'Unable to fetch order status');
      }).finally(() => {
        setLoading(false);
      });
    }, [purchase_order_id, division_id, form_number]);

    let timeFormat = {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };

    const renderStatus = (value, i) => {

        let expectedShipDate = value.expectedShipDate ? new Date(value.expectedShipDate).toLocaleTimeString('en-us', timeFormat) : 'N/a';
        let expectedDeliveryDate = value.expectedDeliveryDate ? new Date(value.expectedDeliveryDate).toLocaleTimeString('en-us', timeFormat) : 'N/a';
        let updatedAt = value.validTimestamp ? new Date(value.validTimestamp).toLocaleDateString('en-us', timeFormat) : 'N/a';

        return (
            <TR key={i} style={{ borderBottom: '1px solid #d7d7d7', background: '#FFF' }}>
                <TD style={{paddingLeft:'7px'}}>{value.factoryOrderNumber}</TD>
                <TD>{value.statusName}</TD>
                <TD>{expectedShipDate}</TD>
                <TD>{expectedDeliveryDate}</TD>
                <TD>{
                  _.map(toArray(_.get(value, 'ResponseToArray.RespondTo') || []), (responder) => {
                    return _.flatMap(
                      _.filter([responder.name, responder.emailAddress, responder.phoneNumber]),
                      (i) => [i, <br/>]
                    );
                  }) || 'N/a'
                }</TD>
                <TD>{value.additionalExplanation ? value.additionalExplanation : 'N/a'}</TD>
                <TD>{value.responseRequired}</TD>
                <TD>{updatedAt}</TD>
            </TR>
        );
    };

    const renderNotifications = (value, i) => {
        const locations = toArray(_.get(value, 'ShipmentLocationArray.ShipmentLocation') || []);

        return (
            <TR key={i} style={{ borderBottom: '1px solid #d7d7d7' }}>
                <TD style={{paddingLeft:'7px'}}>{value.salesOrderNumber}</TD>
                <TD>{value.complete}</TD>
                <TD>{
                  _.map(locations, (location, i) => {
                    return <Address key={i} address={location.ShipFromAddress}/>;
                  })
                }</TD>
                <TD>{
                  _.map(locations, (location, i) => {
                    return <Address key={i} address={location.ShipToAddress}/>;
                  })
                }</TD>
                <TD>{
                  _.map(locations, (location, i) => {
                    return _.map(toArray(_.get(location, 'PackageArray.Package') || []), 'trackingNumber').flatMap((line) => [
                      line, <br/>
                    ]);
                  })
                }</TD>
              <TD>{
                _.map(locations, (location, i) => {
                  return _.map(toArray(_.get(location, 'PackageArray.Package') || []), 'shipmentDate').flatMap((shipmentDate) => [
                    shipmentDate ? new Date(shipmentDate).toLocaleTimeString('en-us', timeFormat) : 'N/a', <br/>
                  ]);
                })
              }</TD>
                <TD>{
                  _.map(locations, (location, i) => {
                    return _.map(toArray(_.get(location, 'PackageArray.Package') || []), 'shipment_method').flatMap((line) => [
                      line, <br/>
                    ]);
                  })
                }</TD>
            </TR>
        );
    };

    return (
        <Theme>
            <Popup
                style={{ padding: '5px 25px', xoverflowY: 'scroll'  }}
                onClose={onClose}
                title={[<span style={{fontWeight: '600', fontSize:'24px', fontFamily: 'TT Norms Pro'}}>Order Status & Shipment Notification </span>, <span style={{fontWeight: '400', fontSize:'16px', fontFamily: 'TT Norms Pro'}}>PO#{data.form_number}</span>]}
                controls={<XIcon onClick={onClose} style={{background:'#FFF', color: '#00A0B6'}}/>}>
          <div className="row" style={{ background: '#edf4f7', paddingTop: '16px' }}>
            {loading ? (
              <div className="row popup-content column">
                <div className="small-12 columns">
                  <div className="small-12 text-center"><br /><img alt="loading..." src="/images/gears.gif" /><br /></div>
                </div>
              </div>
            ) : error ? (
              <>
                <div className="row popup-content column">
                  <div className="small-12 columns">
                    <div
                      style={{
                        margin: '0 16px 16px 16px',
                        padding: '12px',
                        background: '#FFF2F3',
                        color: '#8F000A',
                      }}
                    >
                      <strong>Error: {error}</strong>
                    </div>
                  </div>
                </div>
              </>

            ) :
              (<>
                <StatusTile style={{ float: 'left', marginRight: '16px', marginLeft: '16px' }}>
                  <Row style={{ height: '64px' }}>
                    <Col style={{ color: '#2A4D63', }}>
                      <div style={{ lineHeight: '20px', textAlign: 'left', marginLeft: '20px' }}><SkubotLogo
                        style={{ display: 'inline-block', marginRight: '20px' }}
                        color={'#123952'} />
                        <div style={{
                          display: 'inline-block',
                          width: '70px',
                          fontWeight: '600',
                          verticalAlign: 'top',
                          paddingTop: '23px',
                          color: '#123952'
                        }}>Commonsku
                        </div>
                      </div>
                    </Col>
                    <Col>
                      {recommendation &&
                        <Button
                          style={{ marginLeft: '20px', fontWeight: 'bold' }}
                          size="small"
                          onClick={(e) => {
                            if (recommendation.field === 'standing') {
                              onChange(normalizedData.purchase_order_id, recommendation.field, normalizedData.standing)(recommendation.value);
                            } else {

                              const match = commonskuStatuses.find((status) => status.label === recommendation.value ||  status.value === recommendation.value);
                              if (!match) return; // avoid crash
                              onChange(normalizedData.purchase_order_id, 'status_id', normalizedData.status_id)(match.value);
                            };
                          }}
                        >
                          Set {recommendation.label} to
                          "{recommendation.value}"
                        </Button>}
                    </Col>
                  </Row>
                    <Row style={{ marginTop: '20px' }}>
                      <Col style={{ paddingLeft: '10px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div className="status-stage-order-status-dropdowns"
                          >
                            <StatusDropdown
                              style={{ width: '90px' }}
                              items={tableStandings}
                              value={_.find(tableStandings, { value: normalizedData.standing })}
                              row={normalizedData}
                            />
                          </div>
                          <div className="status-stage-order-status-dropdowns">
                            <StateDropdown
                              style={{ width: '120px' }}
                              items={_.filter(tableStatuses, ({ order, content }) => {
                                return order > 0 || content === normalizedData.status_name;
                              })}
                              value={_.find(tableStatuses, { content: normalizedData.status_name })}
                              row={normalizedData}
                              scrollContentHeight="300px"
                              maxCircles={tableStatuses.length - 2}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col>
                        <p style={{ textAlign: 'left', fontWeight: '400', fontSize: '14px', fontFamily: 'TT Norms Pro' }}>Click on the commonsku status and stage to change them, or click the button above to automatically set the commonsku fields.</p>

                      </Col>
                    </Row>
                    <Row style={{ marginTop: '15px' }}>
                      <Col style={{ textAlign: 'left', paddingLeft: '10px' }}>
                        <span style={{ fontWeight: '600', fontSize: '16px', fontFamily: 'TT Norms Pro' }}>PO Shipping Date:</span>
                        <span style={{ marginLeft: '10px' }}>{formatDateSafe(date_shipped) || 'N/A'}</span>
                      </Col>
                      <Col></Col>
                    </Row>
                </StatusTile>
                <StatusTile style={{ marginRight: '16px' }}>
                  <Row style={{ marginLeft: '10px' }}>
                    <Col>
                      <div style={{ lineHeight: '20px', textAlign: 'left' }}><ConnectedPlusIcon style={{ display: 'inline-block', marginRight: '20px' }} size={'large'} color={'#123952'} /><div style={{ display: 'inline-block', width: '70px', fontWeight: '600', verticalAlign: 'top', paddingTop: '7px', color: '#123952' }}>Connected</div></div>
                    </Col>
                    <Col>
                      <div style={{ display: 'inline-block' }}>
                        <a style={{ textDecoration: 'none', color: '#00A0B6', fontWeight: '600', fontSize: '12px', fontFamily: 'TT Norms Pro' }} href="https://help.commonsku.com/knowledge/production-report-connected-status-real-time-promostandards-updates" target={"_blank"} rel="noopener noreferrer">How does it work</a>
                      </div>
                      <Button
                        style={{ marginLeft: '20px', fontWeight: 'bold', display: 'inline-block' }}
                        size="small" onClick={() => {
                          checkOrderStatus(purchase_order_id, division_id, form_number, true).then((result) => {
                            setStatuses(result.statuses);
                            setNotifications(result.notifications);
                            setError(result.error);
                            if (result.ps_status_name) {
                              setPsStatusName(result.ps_status_name);
                            }
                            if (result.date_valid) {
                              setDateValid(parseMysqlDate(result.date_valid));
                            }
                            if (onPatchRow && (result.ps_status_name || result.date_valid)) {
                              onPatchRow(purchase_order_id,
                                {
                                  ps_status_name: result.ps_status_name ||   normalizedData.ps_status_name,
                                  date_valid: result.date_valid ? parseMysqlDate(result.date_valid) : normalizedData.date_valid
                                }
                              );
                            }
                          });
                        }}
                      >Refresh</Button>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '20px' }}>
                    <Col style={{ textAlign: 'left', marginLeft: '15px' }}>{statusIcon} <span style={{ fontWeight: '600', verticalAlign: 'top', paddingTop: '7px', color: '#123952', marginLeft: '20px' }}>{psStatusName ? psStatusName : 'N/A'}</span></Col>
                  </Row>
                  {moment(dateValid).isValid() && (<Row style={{ lineHeight: '50px' }}>
                    <Col style={{ textAlign: 'left', marginLeft: '10px' }}>
                      <div>
                        Updated {moment(dateValid).isValid() && <span>{parseInt(moment.duration(moment().diff(moment(dateValid))).asHours())}h ago</span>}
                      </div>
                    </Col>
                  </Row>)}
                  <Row style={{ lineHeight: '50px' }}>
                    <Col style={{ textAlign: 'left', marginLeft: '10px' }}>
                      {
                        statuses && statuses.map((value, i) => {
                          return value.additionalExplanation && <div>{value.additionalExplanation}</div>
                        })
                      }
                    </Col>
                  </Row>

                  <Row style={{ lineHeight: '50px' }}>
                    <Col style={{ textAlign: 'left', marginLeft: '10px' }}>
                      <span style={{ fontWeight: '600', fontSize: '16px', fontFamily: 'TT Norms Pro' }}>Suggested Ship Date:</span>
                      {
                        statuses && statuses.map((value, i) => {
                          return value.expectedShipDate && <span style={{ marginLeft: '10px' }}>{moment(value.expectedShipDate).isValid() ? moment(value.expectedShipDate).format('MMMM D, YYYY h:m A') : 'n/a'}</span>
                        })
                      }
                    </Col>
                  </Row>
                </StatusTile>
                <Table style={{ marginTop: '10px' }}>
                  <StyledTHead>
                    <TR>
                      <TH style={{ paddingLeft: '7px' }}>Factory Order Number</TH>
                      <TH>Status Name</TH>
                      <TH>Expected Ship Date</TH>
                      <TH>Expected Delivery Date</TH>
                      <TH>Respond To</TH>
                      <TH>Additional Explanation</TH>
                      <TH>Response Required</TH>
                      <TH>Updated At</TH>
                    </TR>
                  </StyledTHead>
                  <TBody>
                    {statuses && statuses.map((s, i) =>
                      renderStatus(s, i)
                    )}
                  </TBody>
                </Table>
                <Table style={{ paddingTop: '10px' }}>
                  <StyledTHead>
                    <TR >
                      <TH style={{ paddingLeft: '7px' }}>Factory Order Number</TH>
                      <TH>Complete</TH>
                      <TH>Shipping From</TH>
                      <TH>Shipping To</TH>
                      <TH>Tracking Number</TH>
                      <TH>Shipment Date</TH>
                      <TH>Shipment Method</TH>
                    </TR>
                  </StyledTHead>
                  <TBody>
                    {notifications && notifications.map((s, i) =>
                      renderNotifications(s, i)
                    )}
                  </TBody>
                </Table>
              </>)}
          </div>
            </Popup>
        </Theme>
    );
}

const StatusTile = ({children, style={}}) => {
    return <div
        style={{
            textAlign: 'center',
            background: '#FFFFFF',
            boxShadow: '0px 4px 5px rgba(72, 78, 86, 0.1)',
            overflowX: 'auto',
            flexDirection: 'column',
            overflowY: 'hidden',
            width: '48.5%',
            height: '260px',
            borderRadius: '16px',
            paddingTop: '32px',
            ...style,
        }}
    >
        {children}
    </div>
};

export default OrderStatusPopup;
